import { setupLayouts } from 'virtual:generated-layouts'
import { createRouter, createWebHistory } from 'vue-router'
import { isUserLoggedIn } from './utils'
import routes from '~pages'
import { canNavigate } from '@layouts/plugins/casl'
import getUserType from "@/helpers/getUserType"
import { useCookies } from "vue3-cookies"
import { useMainStore } from "@/stores/MainStore"
import { useAuthStore } from "@/stores/AuthStore"

const externalLoginURL = import.meta.env.VITE_EXTERNAL_LOGIN_URL

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // ℹ️ We are redirecting to different pages based on role.
    // NOTE: Role is just for UI purposes. ACL is based on abilities.
    {
      path: '/',
      redirect: to => {
        const { cookies } = useCookies()
        if (localStorage.getItem('userData') && localStorage.getItem('userData') !== 'undefined' && cookies.get('jwt')) {

          const userData = JSON.parse(localStorage.getItem('userData') || '{}')
          const userRole = (userData && userData.role) ? userData.role : null
          const userType = getUserType() === 'retailer' ? 'retailer' : 'affiliate'
          if (userRole !== 'client')
            return { name: `dashboards-${ userType }-view` }
          if (userRole === 'client')
            return { name: 'access-control' }
        }
        
        return { name: 'login', query: to.query }
      },
    },
    {
      path: '/pages/user-profile',
      redirect: () => ({ name: 'pages-user-profile-tab', params: { tab: 'profile' } }),
    },
    {
      path: '/pages/account-settings',
      redirect: () => ({ name: 'pages-account-settings-tab', params: { tab: 'account' } }),
    },
    {
      path: '/landing/details/:id',
      name: 'landing-details',
      component: () => import('@/pages/landing/update/[id].vue'),
    },
    ...setupLayouts(routes),
  ],
})


// Docs: https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
router.beforeEach(async to => {
  const isLoggedIn = isUserLoggedIn()
  const mainStore = useMainStore()
  const currentUrl = new URL(window.location.href)
  const hasToken = currentUrl.searchParams.has('token')
  const authStore = useAuthStore()

  let isAllowedToProceed = false

  /*
  
    ℹ️ Commented code is legacy code
  
    if (!canNavigate(to)) {
      // Redirect to login if not logged in
      // ℹ️ Only add `to` query param if `to` route is not index route
      if (!isLoggedIn)
        return next({ name: 'login', query: { to: to.name !== 'index' ? to.fullPath : undefined } })
  
      // If logged in => not authorized
      return next({ name: 'not-authorized' })
    }
  
    // Redirect if logged in
    if (to.meta.redirectIfLoggedIn && isLoggedIn)
      next('/')
  
    return next()
  
    */

  if (!isLoggedIn) {
    const token = currentUrl.searchParams.get('token')

    if (!hasToken) {
      isAllowedToProceed = false
    } else {
      const savedJWTInfo = await authStore.saveJWTInfo(token)

      if (savedJWTInfo) isAllowedToProceed = true
    }
  } else {
    if (hasToken) {
      const { cookies } = useCookies()
      const newToken = currentUrl.searchParams.get('token')

      if (newToken !== cookies.get("jwt")) {
        await authStore.saveJWTInfo(newToken)
      }
    }
    isAllowedToProceed = true
  }

  if (to.name === 'login' && externalLoginURL && !isAllowedToProceed) {
    // Extract the base URL without query parameters
    const baseUrl = `${currentUrl.origin}${currentUrl.pathname}`
    const returnUrl = encodeURIComponent(baseUrl) // Encode to ensure it's safe for use in a URL

    window.location.href = `${externalLoginURL}?redirect_url=${returnUrl}`

    return // Prevent further navigation
  }

  currentUrl.searchParams.delete('token')
  if (to.query.token) {
    // Create a new query object without the 'token' parameter
    const { token, ...newQuery } = to.query

    // Navigate to the same route without the 'token' parameter
    return { path: to.path, query: newQuery }
  }

  if (canNavigate(to)) {
    if (to.meta.redirectIfLoggedIn && isAllowedToProceed) {
      // Proceed to the route as normal
      return '/'
    } else if (!isAllowedToProceed && (to.name !== 'login' && to.name !== 'forgot-password' && to.name !== 'password-reset')) {
      await mainStore.addMessage('error', 'Your session expired. Please login again')

      return { name: 'login' }
    }
    else {
    }
  } else {
    if (isAllowedToProceed) return { name: 'not-authorized' }
    else return { name: 'login' }
  }


})
export default router
